// The names an operator can pick from while editing a phase: template
// placeholders for scripted emails, and the fields a transition predicate may
// test. Everything here is derived from the strategy document itself plus the
// engine's fixed roots (TemplateContext and PredicateContext on the backend);
// nothing is client vocabulary.

import type { PhaseDocument, StrategyDocument } from "@/lib/types";

export interface VocabularyEntry {
  /** The dotted path as the engine resolves it. */
  path: string;
  /** What the picker shows. */
  label: string;
  /** Picker section. */
  group: string;
}

/** Prospect columns the template namespace exposes (TemplateContext.prospectMap). */
const TEMPLATE_PROSPECT_KEYS = [
  ["email", "email address"],
  ["full_name", "full name"],
  ["first_name", "first name"],
  ["last_name", "last name"],
  ["phone", "phone"],
  ["status", "status"],
  ["timezone", "timezone"],
  ["source", "source"],
] as const;

/** Prospect columns a predicate may test (PredicateContext). */
const PREDICATE_PROSPECT_KEYS = [
  ["id", "id"],
  ["email", "email address"],
  ["status", "status"],
  ["timezone", "timezone"],
  ["source", "source"],
  ["unsubscribed_at", "unsubscribed at"],
] as const;

function customFields(document: StrategyDocument): VocabularyEntry[] {
  const fields = document.strategy?.prospect_schema?.fields ?? [];
  return fields
    .filter((f) => typeof f.key === "string" && f.key !== "")
    .map((f) => ({
      path: `prospect.custom_fields.${f.key}`,
      label: f.label && f.label !== f.key ? `${f.label} (${f.key})` : String(f.key),
      group: "Prospect fields",
    }));
}

/**
 * Placeholders a scripted email or example may use, for one phase: the fixed
 * prospect / sender / strategy roots, the strategy's declared prospect fields,
 * and this phase's computed fields.
 */
export function templateVariables(
  document: StrategyDocument,
  phase: PhaseDocument | undefined,
): VocabularyEntry[] {
  const out: VocabularyEntry[] = TEMPLATE_PROSPECT_KEYS.map(([key, label]) => ({
    path: `prospect.${key}`,
    label,
    group: "Prospect",
  }));
  out.push(...customFields(document));
  out.push({ path: "sender.name", label: "sender name", group: "Sender" });
  out.push({ path: "strategy.name", label: "strategy name", group: "Strategy" });
  for (const cf of phase?.computed_fields ?? []) {
    if (typeof cf.key === "string" && cf.key !== "") {
      out.push({ path: `computed.${cf.key}`, label: cf.key, group: "Computed (this phase)" });
    }
  }
  return out;
}

/**
 * Fields a transition or document-output predicate may test. Qualification
 * keys come from every phase's questions: an answer given in one phase is
 * stored on the prospect and stays testable later in the journey.
 */
export function predicateFields(document: StrategyDocument): VocabularyEntry[] {
  const out: VocabularyEntry[] = PREDICATE_PROSPECT_KEYS.map(([key, label]) => ({
    path: `prospect.${key}`,
    label,
    group: "Prospect",
  }));
  out.push(...customFields(document));

  const seen = new Set<string>();
  for (const p of document.strategy?.phases ?? []) {
    for (const q of p.qualification_questions ?? []) {
      if (typeof q.key === "string" && q.key !== "" && !seen.has(q.key)) {
        seen.add(q.key);
        out.push({ path: `qualification.${q.key}`, label: q.key, group: "Qualification answers" });
      }
    }
  }

  out.push(
    { path: "phase.dwell_minutes", label: "minutes in this phase", group: "Phase" },
    { path: "phase.emails_sent", label: "emails sent this run", group: "Phase" },
    { path: "last_inbound.intent", label: "intent", group: "Last reply" },
    { path: "last_inbound.sentiment", label: "sentiment", group: "Last reply" },
    { path: "last_inbound.objection_detected", label: "objection detected", group: "Last reply" },
    { path: "matched_conditions", label: "classifier condition", group: "Reply classifier" },
  );
  return out;
}

/**
 * Condition names the reply classifier can match for one phase: every legacy
 * rule's `condition`, plus any name already referenced by a compound rule.
 */
export function conditionNames(phase: PhaseDocument | undefined): string[] {
  const names = new Set<string>();
  for (const r of phase?.transition_rules ?? []) {
    if (typeof r.condition === "string" && r.condition !== "") names.add(r.condition);
  }
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node !== "object" || node === null) return;
    const rec = node as Record<string, unknown>;
    if (rec.field === "matched_conditions" && typeof rec.value === "string" && rec.value !== "") {
      names.add(rec.value);
    }
    Object.values(rec).forEach(walk);
  };
  for (const r of phase?.transitions ?? []) walk(r.when);
  return [...names];
}
